import { Component } from '@theme/component';

const config = JSON.parse(document.getElementById('HenryWishlistConfig').textContent);
const storageKey = 'party-delights:wishlist:v1';
const changed = 'henry:wishlist-change';
const legacyKey = 'party-delights-favourites';
const validHandle = (handle) => typeof handle === 'string' && /^[a-z0-9][a-z0-9-]{0,254}$/.test(handle);
const parse = (raw) => {
  try {
    const values = JSON.parse(raw);
    return Array.isArray(values) ? [...new Set(values.filter(validHandle))] : [];
  } catch { return []; }
};
let saved;
try { saved = parse(localStorage.getItem(storageKey)); } catch { saved = []; }
const productCache = new Map();
const root = config.root.endsWith('/') ? config.root : `${config.root}/`;
const money = new Intl.NumberFormat(config.locale, { style: 'currency', currency: config.currency });
const copy = (key, title) => config[key].replace('__PRODUCT__', title);
const announce = (message) => { document.getElementById('HenryWishlistStatus').textContent = message; };

function syncHeader() {
  for (const link of document.querySelectorAll('[data-wishlist-open]')) {
    link.setAttribute('aria-label', `${config.title} ${saved.length}`);
    const badge = link.querySelector('[data-wishlist-count]');
    if (badge) { badge.textContent = String(saved.length); badge.hidden = !saved.length; }
  }
}

function toggle(handle, title) {
  if (!validHandle(handle)) return;
  // Merge other tabs' latest changes before this operation, without losing the
  // in-memory fallback when browser storage is unavailable.
  try { const latest = localStorage.getItem(storageKey); if (latest !== null) saved = parse(latest); } catch { /* Session fallback. */ }
  const removing = saved.includes(handle);
  // Retire legacy saves for this known product so a later visit cannot restore
  // a product the shopper has deliberately removed from the unified wishlist.
  try {
    const id = document.querySelector(`henry-wishlist-toggle[data-handle="${handle}"]`)?.dataset.productId;
    const legacy = JSON.parse(localStorage.getItem(legacyKey) || '[]');
    if (id && Array.isArray(legacy)) localStorage.setItem(legacyKey, JSON.stringify(legacy.filter((item) => item !== id)));
  } catch { /* Storage remains optional. */ }
  saved = removing ? saved.filter((item) => item !== handle) : [handle, ...saved];
  let message = copy(removing ? 'removed' : 'added', title);
  try { localStorage.setItem(storageKey, JSON.stringify(saved)); } catch { message += ` ${config.temporary}`; }
  syncHeader();
  document.dispatchEvent(new CustomEvent(changed));
  announce(message);
}

async function getProduct(handle) {
  if (!productCache.has(handle)) {
    const request = (async () => {
      const response = await fetch(`${root}products/${encodeURIComponent(handle)}.js`, { signal: AbortSignal.timeout(12000) });
      if (response.status === 404) return null;
      if (!response.ok) throw new Error('Wishlist product request failed');
      const product = await response.json();
      if (!product || !product.title || !Number.isFinite(product.price)) throw new Error('Invalid product response');
      return product;
    })();
    productCache.set(handle, request);
    request.catch(() => productCache.delete(handle));
  }
  return productCache.get(handle);
}

function element(tag, className, text) {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
}

function card(handle, product) {
  const title = product?.title || config.unavailable;
  const item = element('article', 'pd-wishlist__card');
  const url = `${root}products/${encodeURIComponent(handle)}`;
  const media = element(product ? 'a' : 'div', 'pd-wishlist__media');
  if (product) { media.href = url; media.setAttribute('aria-label', title); }
  if (product?.featured_image) {
    const image = element('img');
    const imageURL = new URL(product.featured_image, location.origin);
    if (imageURL.protocol === 'https:' || imageURL.protocol === 'http:') {
      imageURL.searchParams.set('width', '750');
      image.src = imageURL.href;
      image.alt = title;
      image.width = 750;
      image.height = 1000;
      image.loading = 'lazy';
      media.append(image);
    }
  }
  const heart = element('henry-wishlist-toggle', 'pd-wishlist-toggle pd-wishlist-toggle--card');
  heart.dataset.handle = handle;
  heart.dataset.title = title;
  if (product) heart.dataset.productId = String(product.id);
  const button = element('button', 'pd-wishlist-toggle__button');
  button.type = 'button';
  const icon = document.querySelector('.pd-wishlist-header svg').cloneNode(true);
  button.append(icon);
  heart.append(button);
  const details = element('div', 'pd-wishlist__details');
  const name = element(product ? 'a' : 'span', 'pd-wishlist__name', title);
  if (product) name.href = url;
  details.append(name);
  if (product) {
    const prices = element('div', 'pd-wishlist__prices');
    const lowest = product.variants?.find((variant) => variant.price === product.price);
    const comparison = lowest?.compare_at_price;
    const onSale = Number.isFinite(comparison) && comparison > product.price;
    const priceText = money.format(product.price / 100);
    const price = element('span', onSale ? 'pd-wishlist__sale' : '', product.price_varies ? config.from.replace('__PRICE__', priceText) : priceText);
    price.prepend(element('span', 'visually-hidden', `${config.price} `));
    prices.append(price);
    if (onSale) {
      const compare = element('s', 'pd-wishlist__compare', money.format(comparison / 100));
      compare.prepend(element('span', 'visually-hidden', `${config.compare} `));
      prices.append(compare);
    }
    details.append(prices);
    if (!product.available) details.append(element('p', 'pd-wishlist__availability', config.sold_out));
  }
  item.append(media, heart, details);
  return item;
}

class WishlistToggle extends Component {
  #controller;
  connectedCallback() {
    super.connectedCallback();
    // Adopt favourites from the earlier draft collection controls when their
    // products are encountered. Store handles so the Ajax API can resolve them.
    try {
      const legacy = JSON.parse(localStorage.getItem(legacyKey) || '[]');
      if (Array.isArray(legacy) && legacy.includes(this.dataset.productId) && !saved.includes(this.dataset.handle)) {
        saved.push(this.dataset.handle);
        localStorage.setItem(storageKey, JSON.stringify(saved));
        syncHeader();
        document.dispatchEvent(new CustomEvent(changed));
      }
    } catch { /* Keep the session usable. */ }
    this.#controller = new AbortController();
    const options = { signal: this.#controller.signal };
    this.addEventListener('click', (event) => {
      if (!event.target.closest('button')) return;
      event.preventDefault();
      event.stopPropagation();
      toggle(this.dataset.handle, this.dataset.title);
    }, options);
    document.addEventListener(changed, () => this.sync(), options);
    this.sync();
  }
  updatedCallback() { super.updatedCallback(); this.sync(); }
  disconnectedCallback() { super.disconnectedCallback(); this.#controller?.abort(); }
  sync() {
    const button = this.querySelector('button');
    if (!button) return;
    const isSaved = saved.includes(this.dataset.handle);
    button.setAttribute('aria-pressed', String(isSaved));
    button.setAttribute('aria-label', copy(isSaved ? 'remove' : 'add', this.dataset.title));
    const label = button.querySelector('[data-wishlist-label]');
    if (label) label.textContent = isSaved ? config.saved : config.save;
  }
}

class WishlistGrid extends Component {
  #controller;
  #revision = 0;
  connectedCallback() {
    super.connectedCallback();
    this.#controller = new AbortController();
    const options = { signal: this.#controller.signal };
    document.addEventListener(changed, () => this.render(), options);
    this.addEventListener('click', (event) => { if (event.target.closest('[data-wishlist-retry]')) this.render(); }, options);
    this.render();
  }
  disconnectedCallback() { super.disconnectedCallback(); this.#controller?.abort(); this.#revision++; }
  async render() {
    if (this.hasAttribute('data-defer') && !this.closest('dialog')?.open) return;
    const revision = ++this.#revision;
    const grid = this.querySelector('[data-wishlist-grid]');
    const empty = this.querySelector('[data-wishlist-empty]');
    const loading = this.querySelector('[data-wishlist-loading]');
    const error = this.querySelector('[data-wishlist-error]');
    const footer = this.querySelector('[data-wishlist-footer]');
    const active = document.activeElement;
    const focusedHandle = this.contains(active) ? active.closest('henry-wishlist-toggle')?.dataset.handle : null;
    const previousHandles = Array.from(grid.querySelectorAll('henry-wishlist-toggle'), (el) => el.dataset.handle);
    const handles = [...saved];
    error.hidden = true;
    empty.hidden = handles.length > 0;
    loading.hidden = handles.length === 0 || grid.childElementCount > 0;
    grid.hidden = handles.length === 0;
    if (footer) footer.hidden = handles.length === 0;
    if (!handles.length) {
      this.removeAttribute('aria-busy');
      grid.replaceChildren();
      if (focusedHandle) empty.querySelector('a').focus();
      return;
    }
    this.setAttribute('aria-busy', 'true');
    const products = new Array(handles.length);
    let next = 0;
    // Bound network concurrency for long saved lists.
    await Promise.all(Array.from({ length: Math.min(6, handles.length) }, async () => {
      while (next < handles.length && revision === this.#revision) {
        const index = next++;
        try { products[index] = await getProduct(handles[index]); } catch { products[index] = undefined; }
      }
    }));
    if (revision !== this.#revision || !this.isConnected) return;
    const fragment = document.createDocumentFragment();
    for (let index = 0; index < handles.length; index++) {
      if (products[index] !== undefined) fragment.append(card(handles[index], products[index]));
    }
    grid.replaceChildren(fragment);
    loading.hidden = true;
    error.hidden = products.every((product) => product !== undefined);
    this.removeAttribute('aria-busy');
    // Removing a card should leave keyboard focus on the next useful action.
    if (focusedHandle && (document.activeElement === document.body || document.activeElement === active)) {
      const buttons = Array.from(grid.querySelectorAll('henry-wishlist-toggle button'));
      const retained = buttons.find((button) => button.parentElement.dataset.handle === focusedHandle);
      (retained || buttons[Math.min(Math.max(0, previousHandles.indexOf(focusedHandle)), buttons.length - 1)] || error.querySelector('button')).focus();
    }
  }
}

class WishlistDialog extends Component {
  #controller;
  #opener;
  connectedCallback() {
    super.connectedCallback();
    this.#controller = new AbortController();
    const options = { signal: this.#controller.signal };
    const dialog = this.querySelector('dialog');
    document.addEventListener('click', (event) => {
      const link = event.target.closest('[data-wishlist-open]');
      if (!link || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
      event.preventDefault();
      this.#opener = link;
      const bar = document.querySelector('.pd-pill-bar');
      const mobile = matchMedia('(max-width: 749px)').matches;
      const top = bar ? (mobile ? bar.getBoundingClientRect().top : bar.getBoundingClientRect().bottom + 16) : 24;
      dialog.style.setProperty('--pd-wishlist-top', `${Math.max(12, Math.min(top, innerHeight - 180))}px`);
      dialog.showModal();
      for (const opener of document.querySelectorAll('[data-wishlist-open]')) opener.setAttribute('aria-expanded', 'true');
      dialog.querySelector('henry-wishlist-grid').render();
    }, options);
    dialog.addEventListener('click', (event) => {
      if (event.target.closest('[data-wishlist-close]')) dialog.close();
      if (event.target === dialog) {
        const bounds = dialog.getBoundingClientRect();
        if (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom) dialog.close();
      }
    }, options);
    dialog.addEventListener('close', () => {
      for (const opener of document.querySelectorAll('[data-wishlist-open]')) opener.setAttribute('aria-expanded', 'false');
      if (this.#opener?.isConnected) this.#opener.focus();
    }, options);
  }
  disconnectedCallback() { super.disconnectedCallback(); this.#controller?.abort(); }
}

customElements.define('henry-wishlist-toggle', WishlistToggle);
customElements.define('henry-wishlist-grid', WishlistGrid);
customElements.define('henry-wishlist-dialog', WishlistDialog);
window.addEventListener('storage', (event) => {
  if (event.key !== storageKey && event.key !== null) return;
  saved = parse(event.newValue);
  syncHeader();
  document.dispatchEvent(new CustomEvent(changed));
});
document.addEventListener('shopify:section:load', syncHeader);
syncHeader();
